import { Variables } from '../../base';
import { escapeHtml } from '../utils/statement-format.utils';
import type { StatementPrintViewModel } from './statement-print.types';

const ENTER_MARK_SVG = `<svg width="86" height="18" viewBox="0 0 77 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M12.8 0V4H0V0H12.8ZM8.576 6.4L12.8 16H8.532L6.4 10.4L4.268 16H0L4.224 6.4H8.576Z" fill="${Variables.Colors.b300}"/>
  <path d="M20.8 2V14H30.3999V11.2853H24.0066V9.21075H29.8106V6.57422H24.0066V4.69517H30.3999V2H20.8Z" fill="${Variables.Colors.n100}"/>
  <path d="M41.9997 2H39.0916V8.12584L34.9701 2H32.3999V14H35.308V7.46365L39.82 14H41.9997V2Z" fill="${Variables.Colors.n100}"/>
  <path d="M43.9998 4.69517H47.2173V14H50.3628V4.69517H53.5996V2H43.9998V4.69517Z" fill="${Variables.Colors.n100}"/>
  <path d="M55.5995 2V14H65.1993V11.2853H58.806V9.21075H64.61V6.57422H58.806V4.69517H65.1993V2H55.5995Z" fill="${Variables.Colors.n100}"/>
  <path d="M70.8171 10.0318L73.3213 14H76.7992L73.9855 9.69976C75.451 9.15302 76.4891 7.90318 76.4891 6.0843C76.4891 3.46488 74.5913 2 72.0104 2H67.1993V14H70.3057V10.0318H70.8171ZM70.3057 4.63653H71.7978C72.9132 4.63653 73.4218 5.20403 73.4218 6.0843C73.4218 6.96457 72.9132 7.53207 71.7978 7.53207H70.3057V4.63653Z" fill="${Variables.Colors.n100}"/>
</svg>`;

const PRINT_STYLES = `
  :root {
    --ps-text: ${Variables.Colors.n100};
    --ps-text-medium: ${Variables.Colors.n70};
    --ps-text-mild: ${Variables.Colors.n50};
    --ps-border: ${Variables.Colors.n15};
    --ps-page: ${Variables.Colors.n5};
    --ps-danger: ${Variables.Swatches.Red6};
    --ps-success: ${Variables.Swatches.Green7};
    --ps-primary: ${Variables.Colors.b300};
    --ps-white: ${Variables.Swatches.White};
  }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    padding: 0;
    font-family: Arial, Helvetica, sans-serif;
    color: var(--ps-text);
    background: transparent;
  }
  .ps-paper {
    width: 816px;
    max-width: 100%;
    margin: 0 auto;
    background: var(--ps-white);
    padding: 52px 60px;
    color: var(--ps-text);
  }
  .ps-masthead {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-bottom: 18px;
    border-bottom: 2px solid var(--ps-text);
  }
  .ps-provider-name {
    font-size: 18px;
    font-weight: 600;
    line-height: 1.2;
    margin-top: 12px;
  }
  .ps-muted { font-size: 12px; color: var(--ps-text-medium); margin-top: 4px; }
  .ps-statement-title {
    font-size: 26px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-align: right;
  }
  .ps-meta {
    margin-top: 14px;
    display: grid;
    grid-template-columns: auto auto;
    gap: 4px 14px;
    justify-content: end;
    font-size: 12px;
    color: var(--ps-text-medium);
  }
  .ps-meta span:nth-child(even) {
    color: var(--ps-text);
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    text-align: right;
  }
  .ps-billto {
    display: grid;
    grid-template-columns: 1fr 264px;
    gap: 24px;
    margin-top: 24px;
    align-items: start;
  }
  .ps-label-sm {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ps-text-mild);
    margin-bottom: 6px;
  }
  .ps-patient-name { font-size: 15px; font-weight: 600; line-height: 1.3; }
  .ps-due-card {
    background: var(--ps-page);
    border: 1px solid var(--ps-border);
    border-radius: 12px;
    padding: 16px 18px;
    text-align: right;
  }
  .ps-due-amount {
    font-size: 30px;
    font-weight: 700;
    margin-top: 6px;
    font-variant-numeric: tabular-nums;
  }
  .ps-due-amount.due { color: var(--ps-danger); }
  .ps-due-amount.paid { color: var(--ps-success); }
  .ps-section {
    margin-top: 24px;
    border: 1px solid var(--ps-border);
    border-radius: 12px;
    overflow: hidden;
  }
  .ps-section-head {
    padding: 9px 16px;
    background: var(--ps-page);
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ps-text-medium);
    border-bottom: 1px solid var(--ps-border);
  }
  .ps-summary-row {
    display: flex;
    justify-content: space-between;
    padding: 7px 16px;
    border-bottom: 1px solid #EEF1F4;
    font-size: 13px;
    color: var(--ps-text-medium);
  }
  .ps-summary-row:last-child { border-bottom: none; }
  .ps-summary-row.emphasize {
    font-weight: 600;
    color: var(--ps-text);
    font-size: 15px;
  }
  .ps-summary-amt { font-variant-numeric: tabular-nums; color: var(--ps-text); font-weight: 500; }
  .ps-summary-amt.credit { color: var(--ps-success); }
  .ps-summary-amt.emphasize { color: var(--ps-danger); font-weight: 700; }
  .ps-activity { margin-top: 28px; }
  .ps-activity-title {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ps-text-medium);
    margin-bottom: 8px;
  }
  .ps-grid-head, .ps-grid-row, .ps-grid-total {
    display: grid;
    grid-template-columns: 78px 1fr 92px 92px 92px;
    gap: 8px;
    padding: 7px 2px;
    align-items: baseline;
  }
  .ps-grid-head {
    border-bottom: 2px solid var(--ps-text);
    font-size: 11px;
    font-weight: 600;
    color: var(--ps-text-medium);
  }
  .ps-grid-row {
    border-bottom: 1px solid #EEF1F4;
    font-size: 12.5px;
  }
  .ps-grid-row.claim-header {
    background: var(--ps-page);
    padding: 8px 10px;
    border-radius: 6px;
    margin-top: 12px;
    border-bottom: none;
  }
  .ps-grid-row.balance-row {
    border-top: 1px solid var(--ps-border);
    border-bottom: none;
    padding-top: 8px;
  }
  .ps-date { font-size: 11px; color: var(--ps-text-mild); font-variant-numeric: tabular-nums; }
  .ps-desc { color: var(--ps-text); }
  .ps-desc.strong { font-weight: 600; }
  .ps-codes { margin-left: 8px; font-size: 10px; font-weight: 600; color: var(--ps-text-mild); font-family: ui-monospace, monospace; }
  .ps-pending {
    margin-left: 8px;
    padding: 1px 7px;
    border-radius: 4px;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    vertical-align: middle;
    background: ${Variables.Colors.b300_l96};
    color: ${Variables.Colors.b300_l62};
    border: 1px solid ${Variables.Colors.b300_l90};
  }
  .ps-pending.warning {
    background: #FBEFD9;
    color: #8A5E1C;
    border: 1px solid #F0D79B;
  }
  .ps-amt { text-align: right; font-variant-numeric: tabular-nums; font-weight: 500; }
  .ps-amt.credit { color: var(--ps-success); }
  .ps-amt.balance-due { font-weight: 700; color: var(--ps-danger); }
  .ps-amt.balance-zero { font-weight: 700; color: var(--ps-text); }
  .ps-grid-total {
    border-top: 2px solid var(--ps-text);
    padding: 11px 2px;
    font-size: 13px;
    font-weight: 600;
  }
  .ps-codekey {
    margin-top: 24px;
    padding: 14px 16px;
    background: var(--ps-page);
    border: 1px solid var(--ps-border);
    border-radius: 12px;
  }
  .ps-codekey-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 28px;
    font-size: 12px;
    color: var(--ps-text-medium);
  }
  .ps-codekey-code { font-weight: 600; font-size: 11px; color: var(--ps-text); font-family: ui-monospace, monospace; }
  .ps-remit {
    margin-top: 30px;
    border-top: 2px dashed var(--ps-border);
    padding-top: 10px;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .ps-remit-note { font-size: 12px; color: var(--ps-text-mild); margin-bottom: 10px; }
  .ps-remit-box {
    border: 1px solid var(--ps-text);
    border-radius: 8px;
    padding: 18px 20px;
    display: grid;
    grid-template-columns: 1fr 230px;
    gap: 18px;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .ps-remit-meta {
    display: grid;
    grid-template-columns: auto auto;
    gap: 7px 12px;
    align-content: start;
    font-size: 12px;
    color: var(--ps-text-medium);
  }
  .ps-remit-meta span:nth-child(even) {
    text-align: right;
    color: var(--ps-text);
    font-variant-numeric: tabular-nums;
  }
  .ps-enclosed { margin-top: 6px; border-bottom: 1px solid var(--ps-text); min-width: 80px; }
  .ps-footer {
    margin-top: 20px;
    text-align: center;
    font-size: 12px;
    color: var(--ps-text-mild);
  }
  .page-break {
    display: block;
    height: 0;
    page-break-before: always;
    break-before: page;
  }
  @page {
    size: letter;
    margin: 0.5in;
  }
  @media print {
    body { background: white; }
    .ps-paper { box-shadow: none; width: 100%; padding: 0.4in 0.5in; }
    .page-break { page-break-before: always; break-before: page; }
    .ps-remit, .ps-remit-box { page-break-inside: avoid; break-inside: avoid; }
  }
`;

export type GenerateStatementPrintHtmlOptions = {
    /** When chunking: omit masthead/summary (continuation pages). */
    showHeader?: boolean;
    /** When chunking: omit code key / remittance / footer until last page. */
    showFooter?: boolean;
    /** Activity rows override (chunk slice). */
    activityRows?: StatementPrintViewModel['activityRows'];
};

export const generateStatementPrintHtml = (
    model: StatementPrintViewModel,
    options: GenerateStatementPrintHtmlOptions = {},
): string => {
    const showHeader = options.showHeader !== false;
    const showFooter = options.showFooter !== false;
    const activityRows = options.activityRows ?? model.activityRows;

    const summaryHtml = model.summaryRows
        .map(row => {
            const amtClass = [
                'ps-summary-amt',
                row.credit ? 'credit' : '',
                row.emphasize ? 'emphasize' : '',
            ]
                .filter(Boolean)
                .join(' ');
            return `<div class="ps-summary-row${row.emphasize ? ' emphasize' : ''}">
        <span>${escapeHtml(row.label)}</span>
        <span class="${amtClass}">${escapeHtml(row.amountText)}</span>
      </div>`;
        })
        .join('');

    const activityHtml = activityRows
        .map(row => {
            const rowClass = [
                'ps-grid-row',
                row.isClaimHeader ? 'claim-header' : '',
                row.isBalanceRow ? 'balance-row' : '',
            ]
                .filter(Boolean)
                .join(' ');
            const descClass = row.isClaimHeader || row.isBalanceRow ? 'ps-desc strong' : 'ps-desc';
            const balanceClass = [
                'ps-amt',
                row.isBalanceRow
                    ? model.amountDueRaw > 0
                        ? 'balance-due'
                        : 'balance-zero'
                    : '',
            ]
                .filter(Boolean)
                .join(' ');

            return `<div class="${rowClass}">
        <div class="ps-date">${escapeHtml(row.dateText)}</div>
        <div>
          <span class="${descClass}">${escapeHtml(row.descText)}</span>
          ${
              row.statusLabel
                  ? `<span class="ps-pending${row.statusTone === 'warning' ? ' warning' : ''}">${escapeHtml(
                        row.statusLabel,
                    )}</span>`
                  : ''
          }
          ${row.codesText ? `<span class="ps-codes">${escapeHtml(row.codesText)}</span>` : ''}
        </div>
        <div class="ps-amt">${escapeHtml(row.chargeText)}</div>
        <div class="ps-amt credit">${escapeHtml(row.creditText)}</div>
        <div class="${balanceClass}">${escapeHtml(row.balanceText)}</div>
      </div>`;
        })
        .join('');

    const codeKeyHtml =
        model.codeKeyItems.length > 0
            ? `<div class="ps-codekey">
        <div class="ps-label-sm" style="margin-bottom:10px;">Understanding your responsibility codes</div>
        <div class="ps-codekey-grid">
          ${model.codeKeyItems
              .map(
                  item =>
                      `<div><span class="ps-codekey-code">${escapeHtml(item.code)}</span> — ${escapeHtml(
                          item.description,
                      )}</div>`,
              )
              .join('')}
        </div>
      </div>`
            : '';

    const headerHtml = showHeader
        ? `
      <div class="ps-masthead">
        <div>
          ${ENTER_MARK_SVG}
          <div class="ps-provider-name">${escapeHtml(model.providerName)}</div>
          <div class="ps-muted">${escapeHtml(model.providerAddress)}</div>
          <div class="ps-muted">${escapeHtml(model.providerEmail)}</div>
        </div>
        <div>
          <div class="ps-statement-title">STATEMENT</div>
          <div class="ps-meta">
            <span>Statement date</span><span>${escapeHtml(model.statementDate)}</span>
            <span>Account #</span><span>${escapeHtml(model.externalId)}</span>
          </div>
        </div>
      </div>

      <div class="ps-billto">
        <div>
          <div class="ps-label-sm">Statement for</div>
          <div class="ps-patient-name">${escapeHtml(model.patientName)}</div>
          <div class="ps-muted">${escapeHtml(model.patientAddress)}</div>
        </div>
        <div class="ps-due-card">
          <div class="ps-label-sm">Amount due</div>
          <div class="ps-due-amount ${model.paidUp ? 'paid' : 'due'}">${escapeHtml(model.amountDue)}</div>
        </div>
      </div>

      <div class="ps-section">
        <div class="ps-section-head">Account summary</div>
        <div>${summaryHtml}</div>
      </div>
    `
        : '';

    const activitySection = `
      <div class="ps-activity">
        ${showHeader ? '<div class="ps-activity-title">Account activity</div>' : ''}
        ${
            showHeader
                ? `<div class="ps-grid-head">
          <div>Date</div><div>Description</div>
          <div style="text-align:right;">Charges</div>
          <div style="text-align:right;">Credits</div>
          <div style="text-align:right;">Balance</div>
        </div>`
                : ''
        }
        ${activityHtml}
        ${
            showFooter
                ? `<div class="ps-grid-total">
          <div></div><div>Total amount due</div><div></div><div></div>
          <div class="ps-amt ${model.paidUp ? 'balance-zero' : 'balance-due'}">${escapeHtml(model.amountDue)}</div>
        </div>`
                : ''
        }
      </div>
    `;

    const footerHtml = showFooter
        ? `
      ${codeKeyHtml}
      <div class="ps-remit">
        <div class="ps-remit-note">Please detach and return the slip below with your payment.</div>
        <div class="ps-remit-box">
          <div>
            <div style="font-weight:600;font-size:14px;">${escapeHtml(model.providerName)}</div>
            <div class="ps-muted">Remit to: ${escapeHtml(model.providerAddress)}</div>
            <div style="margin-top:16px;font-size:12px;">${escapeHtml(model.patientName)}  ·  Account #${escapeHtml(
                  model.externalId,
              )}</div>
          </div>
          <div class="ps-remit-meta">
            <span>Statement date</span><span>${escapeHtml(model.statementDate)}</span>
            <span>Amount due</span><span style="font-weight:700;">${escapeHtml(model.amountDue)}</span>
            <span style="margin-top:6px;">Amount enclosed</span><span class="ps-enclosed">&nbsp;</span>
          </div>
        </div>
      </div>
      <div class="ps-footer">Questions about your statement? Contact ${escapeHtml(
          model.providerEmail,
      )}  ·  Generated ${escapeHtml(model.generatedLabel)}</div>
    `
        : '';

    return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(model.patientName)} - ${escapeHtml(model.statementDate)}</title>
  <style>${PRINT_STYLES}</style>
</head>
<body>
  <div class="ps-paper">
    ${headerHtml}
    ${activitySection}
    ${footerHtml}
  </div>
</body>
</html>`;
};
